import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { AuthorizedRole, KioskAffiliatedUserDto } from 'src/openapi-gen/models';
import { TokenExpiryDto } from 'src/openapi-gen/models/token-expiry-dto';
import { UserMetaDataDto } from 'src/openapi-gen/models/user-meta-data-dto';
import { AccountService } from 'src/openapi-gen/services/account.service';
import { LocalStorageKeys, SessionStorageKeys } from '../core/constants/storage.keys';

/**
 * Important:
 * Before any function call in this class the "refreshUserInformation" method must be called to get the current state
 * of the user's authorized permissions.
 * 
 * See "Auth Guard Service" file for an example.
 */
@Injectable({
  providedIn: "root"
})
export class AuthService {
  private static hasTimerBeenInitialized: boolean = false; 
  private userPermissions: UserMetaDataDto;

  constructor(private accountService: AccountService) {
      this.userPermissions = {
        userName: "",
        rolesList: [],
        affiliatedUserDto: null
      } as UserMetaDataDto;
  }

  public setStorageData(data: TokenExpiryDto) {
    AuthService.SetUserLoggedIn();
    AuthService.RemoveTokenExpirations();

    localStorage.setItem(LocalStorageKeys.AccessTokenExpiryKey, data.accessTokenExpiry);
    localStorage.setItem(LocalStorageKeys.RefreshTokenExpiryKey, data.refreshTokenExpiry);
  }

  public static IsUserLoggedIn(): boolean {
    return localStorage.getItem(LocalStorageKeys.IsUserLoggedIn) === "true";
  }

  public static SetUserLoggedIn() {
    localStorage.setItem(LocalStorageKeys.IsUserLoggedIn, "true");
  }

  // Important: DO NOT CALL THIS METHOD BEFORE AN AUTHORIZED API CALL IS BEING MADE
  public static SetUserLoggedOut() {
    localStorage.clear();
    sessionStorage.clear();
  }

  public static DoTokenExpiriesExist(): boolean {
    return localStorage.getItem(LocalStorageKeys.AccessTokenExpiryKey) !== null &&
           localStorage.getItem(LocalStorageKeys.RefreshTokenExpiryKey) !== null;
  }

  public static GetAccessTokenExpiry(): string {
      return localStorage.getItem(LocalStorageKeys.AccessTokenExpiryKey);
  }

  public static GetRefreshTokenExpiry(): string {
      return localStorage.getItem(LocalStorageKeys.RefreshTokenExpiryKey);
  }

  private static RemoveTokenExpirations() {
      localStorage.removeItem(LocalStorageKeys.AccessTokenExpiryKey);
      localStorage.removeItem(LocalStorageKeys.RefreshTokenExpiryKey);
  }

  public getAffiliatedUser(): KioskAffiliatedUserDto {
    this.refreshFromStorage();
    return this.userPermissions?.affiliatedUserDto ? this.userPermissions.affiliatedUserDto : null;
  }

  public saveAffiliatedUser(data: KioskAffiliatedUserDto) {
    this.refreshFromStorage();
    this.userPermissions.affiliatedUserDto = data;
    sessionStorage.setItem(SessionStorageKeys.UserInfo, JSON.stringify(this.userPermissions));
  }

  public clearAffiliatedUser() {
    this.refreshFromStorage();
    this.userPermissions.affiliatedUserDto = null;
    sessionStorage.setItem(SessionStorageKeys.UserInfo, JSON.stringify(this.userPermissions));
  }

  private refreshFromStorage() {
    let locallyStoredUserInformation = sessionStorage.getItem(SessionStorageKeys.UserInfo);
    this.userPermissions = JSON.parse(locallyStoredUserInformation);
  }

  // If a user's permissions are updated they will need to start a new session by opening up a new tab.
  public async refreshUserInformation() {
    if(!AuthService.IsUserLoggedIn()) {
      return;
    }

    this.initializeClearCredentialsTimer();
    let locallyStoredUserInformation = sessionStorage.getItem(SessionStorageKeys.UserInfo);
    if(locallyStoredUserInformation === null || locallyStoredUserInformation === undefined) {
      let retrievedUserPermissions = await this.getAllUserPermissions();
      this.userPermissions = retrievedUserPermissions;
      sessionStorage.setItem(SessionStorageKeys.UserInfo, JSON.stringify(retrievedUserPermissions));  
    }
    else {
      this.userPermissions = JSON.parse(locallyStoredUserInformation);
    }
  }

  private initializeClearCredentialsTimer() {
    if(AuthService.hasTimerBeenInitialized === false) {
      let tenHourInterval: number = 36000000;
      setInterval(function(){ 
        sessionStorage.removeItem(SessionStorageKeys.UserInfo);
      }, tenHourInterval);

      AuthService.hasTimerBeenInitialized = true;
    }
  }

  public getUserName(): string {
    return this.userPermissions.userName;
  }

  public isUserAuthenticated(): boolean {
    return this.userPermissions.rolesList.length !== 0; 
  }

  public doesUserHaveRole(allowedRoles: AuthorizedRole[]): boolean {
    console.debug(`Auth Guard Roles - ${allowedRoles} | User Roles - ${this.userPermissions.rolesList}`);
    return this.userPermissions.rolesList.some(role => allowedRoles.includes(role)) || this.isUserSuperAdmin();
  }

  public getFullName(): string {
    return this.userPermissions.fullName;
  }

  public getRoleName(): string {
    return this.userPermissions.roleName;
  }

  public getRoles(): string[] {
    return this.userPermissions.rolesList;
  }

  public isUserAdmin(): boolean {
    return this.userPermissions.rolesList.filter(role => role === AuthorizedRole.Admin).length !== 0;
  }

  public isUserSuperAdmin(): boolean {
    return this.userPermissions.rolesList.filter(role => role === AuthorizedRole.SuperAdmin).length !== 0;
  }

  private async getAllUserPermissions(): Promise<UserMetaDataDto>{
    return lastValueFrom(this.accountService.apiAccountUserInfoGet());
  }
}